import { useFetch } from "@/hooks/useFetch";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { rupiah, angka } from "@/lib/format";
import { useNavigate } from "react-router-dom";
import {
  ReceiptText, TrendingUp, FileCheck2, Target, AlertCircle, Wallet, HandCoins, Ban,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
} from "recharts";

export default function Dashboard() {
  const { data, loading } = useFetch("/dashboard");
  const navigate = useNavigate();

  if (loading || !data) return <Loading />;

  const cards = [
    { label: "Invoice Bulan Ini", value: rupiah(data.invoice_bulan_ini.total), sub: `${data.invoice_bulan_ini.count} invoice`, icon: ReceiptText, to: "/invoices", tone: "blue" },
    { label: "Omzet Invoice Bersih", value: rupiah(data.omzet_invoice_bersih), sub: "Terbit − batal − CN + DN", icon: TrendingUp, to: "/reports", tone: "green" },
    { label: "Proforma Belum Dikonversi", value: rupiah(data.proforma_belum_dikonversi.total), sub: `${data.proforma_belum_dikonversi.count} proforma (potensi)`, icon: FileCheck2, to: "/proformas", tone: "amber" },
    { label: "Total Potensi Tagihan", value: rupiah(data.total_potensi_tagihan), sub: "Omzet bersih + proforma", icon: Target, to: "/reports", tone: "blue" },
    { label: "Invoice Belum Dibayar", value: rupiah(data.invoice_belum_dibayar.total), sub: `${data.invoice_belum_dibayar.count} invoice`, icon: AlertCircle, to: "/receivables", tone: "amber" },
    { label: "Total Piutang", value: rupiah(data.total_piutang), sub: "Outstanding", icon: HandCoins, to: "/receivables", tone: "rose" },
    { label: "Pembayaran Bulan Ini", value: rupiah(data.pembayaran_bulan_ini), sub: "Kas masuk", icon: Wallet, to: "/payments", tone: "green" },
    { label: "Invoice Dibatalkan", value: rupiah(data.invoice_dibatalkan.total), sub: `${data.invoice_dibatalkan.count} invoice`, icon: Ban, to: "/invoices", tone: "rose" },
  ];

  const aging = Object.entries(data.aging_piutang).map(([k, v]) => ({ name: k, value: v }));
  const agingColors = ["#9333EA", "#22c55e", "#f59e0b", "#f97316", "#e11d48"];

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Ringkasan keuangan & penagihan CV Putra Cilegon Mandiri" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {cards.map((c) => (
          <Card key={c.label} onClick={() => navigate(c.to)} data-testid={`kpi-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="p-6 cursor-pointer border hover:shadow-md hover:-translate-y-[1px] transition-transform">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium">{c.label}</div>
                <div className="font-display text-2xl font-bold mt-2 tabular-nums truncate">{c.value}</div>
                <div className="text-xs text-muted-foreground mt-1">{c.sub}</div>
              </div>
              <div className={`h-10 w-10 rounded-md flex items-center justify-center shrink-0 ${toneBg(c.tone)}`}>
                <c.icon className="h-5 w-5" />
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-6 lg:col-span-2">
          <h3 className="font-display text-lg font-semibold mb-4">Aging Piutang</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={aging}>
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v) => angka(v / 1000000) + "jt"} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => rupiah(v)} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {aging.map((_, i) => <Cell key={i} fill={agingColors[i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-6">
          <h3 className="font-display text-lg font-semibold mb-4">Ringkasan Nota</h3>
          <div className="space-y-4">
            <Row label="Credit Note" value={rupiah(data.credit_note)} tone="rose" />
            <Row label="Debit Note" value={rupiah(data.debit_note)} tone="green" />
            <Row label="Proforma Aktif" value={`${data.proforma_belum_dikonversi.count} dok`} tone="amber" />
            <Row label="Total Piutang" value={rupiah(data.total_piutang)} tone="blue" />
          </div>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value, tone }) {
  return (
    <div className="flex items-center justify-between py-2 border-b last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={`text-sm font-semibold tabular-nums ${{ rose: "text-rose-600", green: "text-emerald-600", amber: "text-amber-600", blue: "text-blue-600" }[tone]}`}>{value}</span>
    </div>
  );
}

function toneBg(t) {
  return { blue: "bg-violet-500/15 text-violet-300", green: "bg-emerald-500/15 text-emerald-300", amber: "bg-amber-500/15 text-amber-300", rose: "bg-rose-500/15 text-rose-300" }[t];
}

function Loading() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="h-32 rounded-md bg-muted animate-pulse" />
      ))}
    </div>
  );
}
